import {
  Color,
  DoubleSide,
  Euler,
  InstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  Vector3,
} from '@technology/web3d';
import { MAP_STATION_PATH_COLOR } from '../../capabilities/constants/map-station';
import type { StationInfo } from '@trimble/web3d-providers';
import { STATION_PATH_MESH_TYPE } from '@trimble/types';

const HALF = 0.5;

export interface InstancedPathOptions {
  stationsInfo: StationInfo[];
  pathWidth: number;
  pathOpacity: number;
  pathHeightOffset: number;
  renderOrder: number;
  /**
   * Gets the Z coordinate for a station position (allows override for 2D map)
   * @param position - The Vector3 position of the station
   * @returns The Z coordinate as a number
   */
  getStationZ(position: Vector3): number;
  /**
   * Initial color for all instances. Defaults to MAP_STATION_PATH_COLOR.
   */
  initialColor?: Color;
}

export interface PathInstanceData {
  startStationIndex: number;
  endStationIndex: number;
  midpoint: Vector3;
  length: number;
  /** Current width for tracking scale changes (used by hybrid approach) */
  currentWidth?: number;
}

export interface InstancedPathResult {
  mesh: InstancedMesh;
  instanceData: PathInstanceData[];
}

/**
 * Creates an instanced mesh for path segments between consecutive stations.
 * Uses a single draw call for all segments, significantly improving performance.
 *
 * Uses MeshBasicMaterial with built-in instanced color support via setColorAt().
 * Note: Per-instance opacity is not supported - all instances share the same material opacity.
 *
 * @param options - Configuration options for the instanced path
 * @returns InstancedPathResult with mesh and instance data, or null if fewer than 2 stations
 */
export function createInstancedPathMesh(
  options: InstancedPathOptions
): InstancedPathResult | null {
  const {
    stationsInfo,
    pathWidth,
    pathOpacity,
    pathHeightOffset,
    renderOrder,
    getStationZ,
    initialColor = MAP_STATION_PATH_COLOR,
  } = options;

  // eslint-disable-next-line no-magic-numbers
  if (stationsInfo.length < 2) {
    return null;
  }

  // First pass: collect valid segments (same runId)
  const validSegments: {
    startIndex: number;
    endIndex: number;
    start: Vector3;
    end: Vector3;
  }[] = [];

  for (let i = 0; i < stationsInfo.length - 1; i++) {
    const currentStation = stationsInfo[i];
    const nextStation = stationsInfo[i + 1];

    // Skip if runIds don't match
    if (currentStation.runId !== nextStation.runId) {
      continue;
    }

    const start = currentStation.position;
    const end = nextStation.position;

    // Use getStationZ to get the correct Z coordinate (allows override for 2D map)
    const startAdjusted = new Vector3(
      start.x,
      start.y,
      getStationZ(start) - pathHeightOffset
    );

    const endAdjusted = new Vector3(
      end.x,
      end.y,
      getStationZ(end) - pathHeightOffset
    );

    validSegments.push({
      startIndex: i,
      endIndex: i + 1,
      start: startAdjusted,
      end: endAdjusted,
    });
  }

  if (validSegments.length === 0) {
    return null;
  }

  const instanceCount = validSegments.length;

  // Create a unit plane geometry (1x1, will be scaled per instance)
  const geometry = new PlaneGeometry(1, 1);

  // Create material - use MeshBasicMaterial with vertexColors for per-instance color
  const material = new MeshBasicMaterial({
    color: 0xffffff, // White base - per-instance colors will override
    transparent: true,
    opacity: pathOpacity,
    side: DoubleSide,
    depthTest: false,
    depthWrite: false,
  });

  // Create instanced mesh
  const mesh = new InstancedMesh(geometry, material, instanceCount);
  mesh.userData = { type: STATION_PATH_MESH_TYPE };
  mesh.renderOrder = renderOrder;

  // Store instance data for later reference (picking, updates)
  const instanceData: PathInstanceData[] = [];

  // Reusable helper objects for matrix calculation (avoids GC pressure)
  const matrix = new Matrix4();
  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();
  const direction = new Vector3();
  const euler = new Euler(0, 0, 0, 'ZYX');

  // Set up each instance
  for (let i = 0; i < validSegments.length; i++) {
    const segment = validSegments[i];

    // Calculate midpoint
    position.set(
      (segment.start.x + segment.end.x) * HALF,
      (segment.start.y + segment.end.y) * HALF,
      (segment.start.z + segment.end.z) * HALF
    );

    // Calculate direction and length
    direction.subVectors(segment.end, segment.start);
    const length = direction.length();
    direction.normalize();

    const yaw = Math.atan2(direction.y, direction.x);
    const pitch = Math.asin(-direction.z);

    euler.set(0, pitch, yaw);
    quaternion.setFromEuler(euler);

    // Scale: length along X, pathWidth along Y (fixed width)
    scale.set(length, pathWidth, 1);

    // Compose matrix and set
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);

    // Set initial color
    mesh.setColorAt(i, initialColor);

    // Store instance data
    instanceData.push({
      startStationIndex: segment.startIndex,
      endStationIndex: segment.endIndex,
      midpoint: position.clone(),
      length,
    });
  }

  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) {
    mesh.instanceColor.needsUpdate = true;
  }

  return {
    mesh,
    instanceData,
  };
}

/**
 * Updates the color of a specific path instance using setColorAt.
 * Remember to set mesh.instanceColor.needsUpdate = true after batch updates.
 */
export function updateInstanceColor(
  mesh: InstancedMesh,
  instanceIndex: number,
  color: Color
): void {
  mesh.setColorAt(instanceIndex, color);
}
